import type { CropCorners } from './types.ts';

/**
 * Deterministically order 4 corners as TL, TR, BR, BL.
 */
export function orderCorners(pts: [number, number][]): CropCorners {
  if (pts.length !== 4) {
    throw new Error(`Expected 4 points, got ${pts.length}`);
  }

  // TL has min (x + y), BR has max (x + y)
  // TR has min (y - x), BL has max (y - x)
  const sums = pts.map(([x, y]) => x + y);
  const diffs = pts.map(([x, y]) => y - x);

  let tlIdx = 0, brIdx = 0, trIdx = 0, blIdx = 0;
  for (let i = 1; i < 4; i++) {
    if (sums[i] < sums[tlIdx]) tlIdx = i;
    if (sums[i] > sums[brIdx]) brIdx = i;
    if (diffs[i] < diffs[trIdx]) trIdx = i;
    if (diffs[i] > diffs[blIdx]) blIdx = i;
  }

  return [pts[tlIdx], pts[trIdx], pts[brIdx], pts[blIdx]];
}

/**
 * Validate that 4 points form a non-degenerate, strictly convex quadrilateral.
 */
export function validateConvexCorners(corners: [number, number][]): boolean {
  if (!corners || corners.length !== 4) return false;

  // 1. Check pairwise uniqueness
  for (let i = 0; i < 4; i++) {
    for (let j = i + 1; j < 4; j++) {
      const dx = corners[i][0] - corners[j][0];
      const dy = corners[i][1] - corners[j][1];
      if (Math.hypot(dx, dy) < 1.0) {
        return false;
      }
    }
  }

  // 2. Check cross products of consecutive edges
  // Cyclic order: 0->1, 1->2, 2->3, 3->0
  const edges: [number, number][] = [
    [corners[1][0] - corners[0][0], corners[1][1] - corners[0][1]],
    [corners[2][0] - corners[1][0], corners[2][1] - corners[1][1]],
    [corners[3][0] - corners[2][0], corners[3][1] - corners[2][1]],
    [corners[0][0] - corners[3][0], corners[0][1] - corners[3][1]],
  ];

  const crossProducts: number[] = [];
  for (let i = 0; i < 4; i++) {
    const e1 = edges[i];
    const e2 = edges[(i + 1) % 4];
    crossProducts.push(e1[0] * e2[1] - e1[1] * e2[0]);
  }

  const allPositive = crossProducts.every((c) => c > 1e-6);
  const allNegative = crossProducts.every((c) => c < -1e-6);

  if (!allPositive && !allNegative) {
    return false;
  }

  // 3. Shoelace area check
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    area += corners[i][0] * corners[j][1];
    area -= corners[j][0] * corners[i][1];
  }
  area = Math.abs(area) / 2;

  return area >= 10.0;
}
